<template>
    <div :class="requiresMargin ? `mb-4` : ''">
        <!-- Named by aria-labelledby rather than `for`. A `for` label hands its
             clicks to the control, so brushing the caption focused the field (or,
             on a select, threw the menu open) when the user only meant to click
             a word. Assistive tech gets the same accessible name either way. -->
        <label :id="labelId" class="text-primary-200 mb-3 flex items-center justify-between gap-3 text-sm uppercase tracking-wide" v-if="labelStr">
            <div class="flex items-center gap-2">
                <t :path="labelStr" />

                <div v-if="helpTip">
                    <fa :icon="['fas', 'circle-question']" fixed-size v-floating-tooltip="helpTip" />
                </div>
            </div>

            <slot name="labelSuffix" />
        </label>

        <div
            class="flex items-center"
            :class="[
                sensitive ? 'group relative' : '',
                prefix || suffix ? 'bg-primary-500 ring-primary-400/20 overflow-hidden rounded ring-1 ring-inset' : '',
            ]"
        >
            <div
                v-if="prefix"
                v-floating-tooltip="[prefix, 'top', true]"
                class="form-input-affix bg-primary-800 text-primary-300 border-primary-400/20 flex min-h-8 min-w-0 shrink items-center self-stretch border-r px-3 font-mono text-xs"
            >
                <span class="truncate">{{ prefix }}</span>
            </div>

            <span class="min-w-0 flex-1" v-floating-tooltip="tippy" v-if="permission" tabindex="0">
                <input
                    class="input"
                    :class="[prefix || suffix ? '!ring-0' : '', ...inputClasses]"
                    v-model="input"
                    :id="id"
                    :aria-labelledby="labelStr ? labelId : undefined"
                    :type="type"
                    :name="name"
                    :step="step"
                    :min="min"
                    :max="max"
                    :placeholder="placeholder ? t(placeholder) : ''"
                    :disabled="!hasPerms || inputDisabled"
                    :readonly="readonly"
                    :checked="checked"
                    :autofocus="autoFocus"
                    :accept="accept"
                    @blur="blur"
                    @focus="emitFocus"
                    @input="emitUpdate"
                    @keydown="emitKeyDown"
                    @keyup="emitKeyUp"
                />
            </span>

            <input
                class="input min-w-0 flex-1"
                :class="[prefix || suffix ? '!ring-0' : '', ...inputClasses]"
                v-model="input"
                :id="id"
                :aria-labelledby="labelStr ? labelId : undefined"
                :type="type"
                :name="name"
                :step="step"
                :min="min"
                :max="max"
                :placeholder="placeholder ? t(placeholder) : ''"
                :disabled="inputDisabled"
                :readonly="readonly"
                :autofocus="autoFocus"
                @blur="blur"
                @change="change"
                @focus="emitFocus"
                @input="emitUpdate"
                @keydown="emitKeyDown"
                @keyup="emitKeyUp"
                v-else
            />

            <div
                v-if="suffix"
                class="form-input-affix bg-primary-800 text-primary-300 border-primary-400/20 flex min-h-8 min-w-0 shrink items-center self-stretch border-l px-3 text-sm"
            >
                <span class="truncate"><t :path="suffix" /></span>
            </div>

            <div v-if="$slots.default" class="ml-4 shrink-0">
                <slot />
            </div>

            <!--
                Sensitive overlay: renders a same-length string of random glyphs over the input,
                blurred and faded out on hover/focus. The mask defeats deblur attacks because the
                pixels under the blur belong to junk text, not the real value.
            -->
            <div
                v-if="sensitive"
                aria-hidden="true"
                class="bg-primary-500 text-primary-200 blur-xs pointer-events-none absolute inset-0 flex select-none items-center overflow-hidden whitespace-nowrap rounded px-3 py-1.5 opacity-100 transition-opacity duration-150 group-focus-within:opacity-0 group-active:opacity-0"
            >
                {{ maskedValue }}
            </div>
        </div>

        <p class="small text-primary-300 mt-1" v-if="footer">
            <t :path="footer" />
        </p>

        <div class="text-danger-300" v-if="inputErrors && !formGlobal">
            <p v-for="(error, idx) of inputErrors" :key="idx">
                <t :path="error" />
            </p>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, onBeforeMount, onBeforeUnmount, PropType, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Validator } from '~/core';
import { translateRequiresPermissions, usePermissionsStore } from '~/plugins/permission';
// TranslatableMessage is globally declared

export default defineComponent({
    name: 'FormInput',

    emits: ['update:value', 'keydown', 'keyup', 'focus'],
    props: {
        name: {
            type: String,
            required: true,
        },
        value: {
            type: [String, Number],
        },
        placeholder: {
            type: String,
        },
        label: {
            type: [String, Array],
        },
        hideLabel: {
            type: Boolean,
        },
        prefix: {
            type: String,
        },
        suffix: {
            type: [String, Array],
        },
        noMargin: {
            type: Boolean,
        },
        footer: {
            type: String,
        },
        type: {
            type: String,
        },
        step: {
            type: [String, Number],
        },
        min: {
            type: [String, Number],
        },
        max: {
            type: [String, Number],
        },
        rule: {
            type: String,
        },
        disabled: {
            type: Boolean,
        },
        checked: {
            type: Boolean,
        },
        readonly: {
            type: Boolean,
        },
        permission: {
            type: [String, Array] as PropType<string | string[]>,
        },
        autoFocus: {
            type: Boolean,
            default: false,
        },
        accept: {
            type: String,
        },
        inputClasses: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        helpTip: {
            type: String,
        },
        tooltipPlacement: {
            type: String,
            validator: () => true, // TODO: List of allowed placements - cba writing long array rn
            default: 'bottom',
        },
        sensitive: {
            type: Boolean,
            default: false,
        },
    },

    setup(props, { emit }) {
        const permissions = usePermissionsStore();
        const { t } = useI18n();
        const localId = useId();

        // Nullable: a `:value` bound straight to a nullable model field arrives
        // as null, and the watcher below assigns it through.
        const input = ref<string | number | null>(props.value ?? '');
        const update = (newValue: string | number | null | undefined) => {
            if (newValue === undefined) return;

            input.value = newValue;
        };
        watch(() => props.value, update);

        const maskedValue = ref('');
        const maskChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        watch(
            () => [props.sensitive, input.value] as const,
            ([sensitive, value]) => {
                if (!sensitive) {
                    maskedValue.value = '';
                    return;
                }

                const length = String(value ?? '').length;
                const bytes = crypto.getRandomValues(new Uint8Array(length));
                maskedValue.value = Array.from(bytes, (b) => maskChars[b % maskChars.length]).join('');
            },
            { immediate: true },
        );

        const inputDisabled = ref(!!props.disabled);
        watch(
            () => props.disabled,
            (newDisabled) => {
                inputDisabled.value = !!newDisabled;
            },
        );
        const inputErrors = ref<null | TranslatableMessage[][]>(null);

        const labelStr = computed(() => {
            if (props.type === 'hidden' || props.hideLabel) return null;
            if (props.label) return props.label;

            return `components.form.fields.${props.name}`;
        });

        const formGlobal = inject<boolean>('formGlobal', false);
        const displayFormErrors = inject<displayFormErrorsFn | null>('displayFormErrors', null);
        const validate = () => {
            if (!props.rule) return;

            const { errors } = Validator.formValidate(props.name, input.value, props.rule, formGlobal);

            inputErrors.value = errors || [];
            displayFormErrors?.();
        };

        let unregister: any;

        onBeforeMount(() => {
            unregister = inject<registerFormComponentFn | null>(
                'registerFormComponent',
                null,
            )?.({
                key: props.name,
                // A visible prefix is part of the submitted form value. Avoid
                // concatenating without one so nullable and numeric values keep
                // their original types.
                value: computed(() => (props.prefix ? props.prefix + (input.value ?? '') : input.value)),
                errors: inputErrors,
                validate,
                rule: props.rule,
                onSubmit: (state) => {
                    if (props.disabled) return;

                    inputDisabled.value = state;
                },
            });
        });

        onBeforeUnmount(() => {
            unregister?.();
            unregister = null;
        });

        const hasPerms = computed(() => (props.permission ? permissions.has(props.permission) : true));
        return {
            t,
            input,
            maskedValue,
            labelStr,
            formGlobal,
            inputErrors,
            inputDisabled,
            id: computed(() => `${localId}-${props.name}-input`),
            labelId: computed(() => `${localId}-${props.name}-input-label`),
            requiresMargin: computed(() => {
                if (props.type === 'hidden' || props.hideLabel || props.noMargin) {
                    return false;
                }

                return true;
            }),
            change: (evt: Event) => {
                /*
                 * Autofill on iOS triggers this event, so we need to check whether the value was changed
                 * and update it if so.
                 *
                 * @see https://github.com/wisp-gg/frontend/issues/61
                 */
                const target = evt.target as HTMLInputElement;
                if (input.value !== target.value) input.value = target.value;
            },
            blur: () => validate(),
            emitUpdate: () => emit('update:value', input.value),
            emitFocus: (evt: FocusEvent) => emit('focus', evt),
            emitKeyDown: (evt: KeyboardEvent) => emit('keydown', evt),
            emitKeyUp: (evt: KeyboardEvent) => emit('keyup', evt),

            hasPerms,
            tippy: computed(() => {
                if (props.permission && !hasPerms.value) {
                    return [translateRequiresPermissions(props.permission), props.tooltipPlacement, true];
                }

                return null;
            }),
        };
    },
});
</script>

<style scoped>
.form-input-affix {
    max-width: 50%;
}
</style>
